import moment from 'moment';

/**
 * Shared display formatters for the list tables.
 *
 * `formatTimeFromSeconds` was previously defined five times over — in
 * listAgents, ListCampaign, ListCalls, ListChat and ListTestCalls — as
 * byte-identical copies. All five shared the same defect: they used
 * `moment.duration(...).minutes()`, which returns minutes *within the hour*, so
 * a 65-minute call rendered as "5 minutes 0 seconds" and the hour was silently
 * dropped. `formatDuration` below carries hours explicitly.
 *
 * They also produced strings like "1 minutes 14 seconds" — grammatically wrong,
 * and ~20 characters in a table column that has room for about 8.
 */

/**
 * Compact, sortable-looking duration: "0s", "44s", "1m 14s", "1h 05m".
 *
 * Seconds are dropped past the hour mark — at that scale they are noise, and the
 * column stays narrow.
 *
 * @param {number} totalSeconds
 * @returns {string}
 */
export const formatDuration = (totalSeconds) => {
    const secs = Math.max(0, Math.round(Number(totalSeconds) || 0));

    const hours = Math.floor(secs / 3600);
    const minutes = Math.floor((secs % 3600) / 60);
    const seconds = secs % 60;

    if (hours > 0) return `${hours}h ${String(minutes).padStart(2, '0')}m`;
    if (minutes > 0) return `${minutes}m ${String(seconds).padStart(2, '0')}s`;
    return `${seconds}s`;
};

/**
 * Kept as a named alias so the five call sites read the same as before and the
 * change is a pure formatting swap rather than a rename.
 */
export const formatTimeFromSeconds = formatDuration;

/** "11 Aug 2026" — unambiguous across locales, and narrower than 11-08-2026. */
export const formatDate = (value) =>
    value ? moment.utc(value).local().format('DD MMM YYYY') : 'N/A';

/** "7:13 PM" — rendered under the date so the cell stays narrow. */
export const formatTime = (value) =>
    value ? moment.utc(value).local().format('h:mm A') : '';

/**
 * Splits a destination into a primary identifier and a secondary detail.
 *
 * The Calls list mixes E.164 numbers (`+16094398586`, ~12 chars) with SIP URIs
 * (`sip:447537150530@sip.lax.didlogic.net`, ~37 chars). Because the column hugs
 * its widest value, a handful of SIP rows stretched it to roughly three times
 * the width a phone number needs, leaving visible slack on every other row.
 *
 * The user part is the identifier that matters in a list; the trunk host is
 * routing detail. Returning them separately lets the cell show the host as a
 * muted second line — same treatment as date/time — so the column sizes to the
 * user part while keeping the full value on screen.
 *
 * @param {string} value
 * @returns {{ primary: string, secondary: string }}
 */
export const splitDestination = (value) => {
    if (!value) return { primary: 'N/A', secondary: '' };

    const raw = String(value).trim();

    // sip:user@host or sips:user@host — also tolerates a bare user@host.
    const match = raw.match(/^(?:sips?:)?([^@]+)@(.+)$/i);
    if (match) return { primary: match[1], secondary: match[2] };

    return { primary: raw, secondary: '' };
};
